import { inject, Injectable } from '@angular/core';
import { createORPCClient, onError, ORPCError } from '@orpc/client';
import { RPCLink, RPCLinkOptions } from '@orpc/client/fetch';
import {
  ClientRetryPlugin,
  ClientRetryPluginContext,
} from '@orpc/client/plugins';
import { ContractRouterClient } from '@orpc/contract';
import { Auth } from '@angular/fire/auth';

import { EnhancedLoadingService } from './enhanced-loading.service';
import type { contract } from '#backend/contract';
import { environment } from '../../../environments/environment';
import { SnackbarService } from './snackbar.service';

/** Contexto client-side (não vai pro servidor) — permite customizar o alert do interceptor por chamada */
export interface OrpcClientContext extends ClientRetryPluginContext {
  errorMessage?: string;
  /** Quando true, o interceptor não exibe alert — usar quando o call site já trata o erro manualmente (ex: erros de negócio com mensagens específicas por código) */
  suppressErrorAlert?: boolean;
}

// Função para criar fetch customizado com loading
function createFetchWithLoading(loadingService: EnhancedLoadingService) {
  return async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    loadingService.show();
    try {
      // 'include' para o cookie de sessão do manual de instruções sobreviver a um request
      // cross-origin localmente (frontend na 4200, backend na 3000) — sem isso o navegador
      // ignora o Set-Cookie de uma resposta cross-origin. Nos ambientes de nuvem BACKEND_HOST
      // é same-origin, então não muda nada lá.
      const response = await fetch(input, { ...init, credentials: 'include' });
      return response;
    } finally {
      loadingService.hide();
    }
  };
}

@Injectable({
  providedIn: 'root',
})
export class OrpcService {
  private auth = inject(Auth);
  private loadingService = inject(EnhancedLoadingService);
  private snackBar = inject(SnackbarService);

  public client: ContractRouterClient<typeof contract, OrpcClientContext> =
    createORPCClient(
      new RPCLink<OrpcClientContext>({
        url: environment.BACKEND_HOST + '/orpc',
        headers: async () => {
          const authorization = await getAuthorizationHeader(this.auth);
          return authorization ? { Authorization: authorization } : {};
        },
        plugins: [createAuthRetryPlugin(this.auth)],
        interceptors: [createErrorInterceptor(this.snackBar)],

        fetch: createFetchWithLoading(this.loadingService),
      }),
    );
}
export const getAuthorizationHeader = async (auth: Auth): Promise<string> => {
  const token = await auth.currentUser?.getIdToken();
  return token ? `Bearer ${token}` : '';
};

export const createErrorInterceptor = (
  snackBar: SnackbarService,
): NonNullable<RPCLinkOptions<OrpcClientContext>['interceptors']>[number] =>
  onError((error, options) => {
    if (options.context?.suppressErrorAlert) return;
    snackBar.handleOrpcError(error, options.context?.errorMessage);
  });

/**
 * O SDK do Firebase agenda a renovação do ID token com um timer; se a máquina
 * entra em sleep, esse timer não dispara e ao acordar o token fica expirado
 * (mesmo com o refresh token da sessão ainda válido). Isso causa UNAUTHORIZED
 * mesmo sem a sessão ter realmente expirado. Por isso, antes de considerar a
 * sessão morta (ver handleOrpcError -> signOut), tentamos forçar a renovação
 * do token e repetir a chamada uma única vez.
 */
export const createAuthRetryPlugin = (auth: Auth) =>
  new ClientRetryPlugin<OrpcClientContext>({
    default: {
      retry: 1,
      // Força a renovação do token e só então libera o retry (o oRPC não
      // espera o retorno de onRetry, então o refresh precisa acontecer aqui
      // para garantir que o header da nova tentativa já leve o token novo).
      retryDelay: async () => {
        await auth.currentUser?.getIdToken(true);
        return 0;
      },
      shouldRetry: ({ attemptIndex, error }) =>
        attemptIndex === 0 &&
        error instanceof ORPCError &&
        error.code === 'UNAUTHORIZED',
    },
  });
